import { useGetCateringCategoryProductsByCatIdQuery } from "@/services/api/event/cateringCategoryApi";
import { useGetCateringPackagesDropdownQuery } from "@/services/api/event/cateringPackageApi";
import { Modal } from "antd";
import { useMemo, useState, useEffect } from "react";
const AddPackageModal = ({ open, onClose, onAddPackage }) => {
  const [selectedPackage, setSelectedPackage] = useState("");
  const { data: packagesResponse, isLoading: packagesLoading } =
    useGetCateringPackagesDropdownQuery({ skip: !open });
  const {
    data: packageDetailsResponse,
    isLoading: isLoadingDetails,
    error: packageDetsError,
  } = useGetCateringCategoryProductsByCatIdQuery(selectedPackage, {
    skip: !selectedPackage || !open,
  });

  const [selections, setSelections] = useState({});
  const [packageLimits, setPackageLimits] = useState({
    min: 0,
    max: 0,
  });
  const [packageImageUrl, setPackageImageUrl] = useState("");

  // Reset selections when package changes
  useEffect(() => {
    if (selectedPackage) {
      setSelections({});
    }
  }, [selectedPackage]);

  // Set package limits and image from API
  useEffect(() => {
    if (packageDetailsResponse) {
      setPackageLimits({
        min: packageDetailsResponse.minimum_serving_size || 0,
        max: packageDetailsResponse.maximum_serving_size || 0,
      });
      setPackageImageUrl(packageDetailsResponse.url || "");
    }
  }, [packageDetailsResponse]);

  const packages = useMemo(() => {
    if (!packagesResponse) return [];
    return packagesResponse.map((cateringpackage) => ({
      value: cateringpackage.id,
      label: cateringpackage.name,
    }));
  }, [packagesResponse]);

  // Transform API data into categories
  const categoryData = useMemo(() => {
    if (!packageDetailsResponse?.catering_categories) return [];

    return packageDetailsResponse.catering_categories.map((category) => ({
      id: category.id,
      name: category.name,
      products: category.catering_products.map((product) => ({
        id: product.id,
        name: product.name,
        price: parseFloat(product.price),
      })),
    }));
  }, [packageDetailsResponse]);

  // This handles the selection state for individual items (checked/unchecked)
  const toggleItem = (categoryId, item) => {
    setSelections((prev) => {
      const categorySelections = prev[categoryId] || [];
      const exists = categorySelections.find((i) => i.id === item.id);

      if (exists) {
        // If item exists, remove it
        return {
          ...prev,
          [categoryId]: categorySelections.filter((i) => i.id !== item.id),
        };
      } else {
        // If item doesn't exist, add it with a default quantity of 1
        return {
          ...prev,
          [categoryId]: [...categorySelections, { ...item, quantity: 1 }],
        };
      }
    });
  };

  const getCategoryTotal = (categoryId) => {
    const categorySelections = selections[categoryId] || [];
    return categorySelections.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
  };

  const getCategoryValidation = (categoryId) => {
    const total = getCategoryTotal(categoryId);
    const { min, max } = packageLimits;

    return {
      total,
      isValid: total >= min && total <= max,
      isUnderMin: total < min,
      isOverMax: total > max,
    };
  };

  const calculateTotalAmount = () => {
    let total = 0;
    Object.keys(selections).forEach((categoryId) => {
      const categorySelections = selections[categoryId] || [];
      categorySelections.forEach((item) => {
        total += (item.price || 0) * (item.quantity || 0);
      });
    });
    return total;
  };

  const canSubmit = () => {
    if (!selectedPackage) return false;

    // Check if all categories meet the min/max requirements
    return categoryData.every((category) => {
      const validation = getCategoryValidation(category.id);
      return validation.isValid;
    });
  };

  const handleSubmit = () => {
    if (!canSubmit()) return;

    const packageInfo = packages.find(
      (pkg) => pkg.value === parseInt(selectedPackage)
    );

    const totalAmount = calculateTotalAmount();

    const packageData = {
      packageId: selectedPackage,
      packageName: packageInfo?.label || "",
      packageImageUrl: packageImageUrl,
      selections, // Raw selections object (categoryId -> products array)
      totalAmount,
      totalPeople: getCategoryTotal(categoryData[0]?.id), // Using first category as reference
      categories: categoryData.map((cat) => ({
        id: cat.id,
        name: cat.name,
        total: getCategoryTotal(cat.id),
        selectedProducts: selections[cat.id] || [], // Detailed products with quantities
      })),
    };

    onAddPackage(packageData);
    setSelections({});
    setSelectedPackage("");
    setPackageImageUrl("");
    onClose();
  };

  const CategorySection = ({ category }) => {
    const validation = getCategoryValidation(category.id);
    const { min, max } = packageLimits;

    // A separate state for managing local input quantity when it's actively being typed
    // This will be null if not actively focused, or hold the string value
    const [localQuantities, setLocalQuantities] = useState({});

    // When selections change, update localQuantities to reflect the global state
    useEffect(() => {
      const newLocalQuantities = {};
      (selections[category.id] || []).forEach((item) => {
        newLocalQuantities[item.id] = item.quantity;
      });
      setLocalQuantities(newLocalQuantities);
    }, [selections, category.id]);

    const handleLocalQuantityChange = (itemId, value) => {
      // Update local state immediately for smooth typing
      setLocalQuantities((prev) => ({
        ...prev,
        [itemId]: value,
      }));
    };

    const handleLocalQuantityBlur = (itemId, value) => {
      // On blur, parse the value and update the global selections state
      const quantity = parseInt(value) || 0;
      setSelections((prev) => {
        const categorySelections = prev[category.id] || [];
        const existingItem = categorySelections.find(
          (item) => item.id === itemId
        );

        if (existingItem) {
          return {
            ...prev,
            [category.id]: categorySelections.map((item) =>
              item.id === itemId ? { ...item, quantity } : item
            ),
          };
        }
        return prev;
      });
      // Optionally, reset local state after blur if desired, or let useEffect handle sync
    };

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full border-2 border-orange-500 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
            </div>
            <h4 className="font-medium text-orange-500">{category.name}</h4>
          </div>
          <span className="text-sm text-gray-500">
            Choose {min} - {max} servings
          </span>
        </div>

        {category.products.map((item) => {
          const isSelected = selections[category.id]?.find(
            (i) => i.id === item.id
          );
          // Use local quantity if available, otherwise fallback to global
          const currentQuantity =
            localQuantities[item.id] !== undefined
              ? localQuantities[item.id]
              : isSelected
              ? isSelected.quantity
              : ""; // Empty string for initial non-selected

          return (
            <div
              key={item.id} // Ensure key is stable and unique for list items
              className="flex items-center justify-between py-2 border-b"
            >
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="checkbox"
                  checked={!!isSelected}
                  onChange={() => toggleItem(category.id, item)}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <span className="text-sm">{item.name}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    (₦{item.price.toLocaleString()}/serving)
                  </span>
                </div>
              </div>

              {isSelected && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={currentQuantity}
                    onChange={(e) =>
                      handleLocalQuantityChange(item.id, e.target.value)
                    }
                    onBlur={(e) =>
                      handleLocalQuantityBlur(item.id, e.target.value)
                    }
                    className="w-20 px-2 py-1 border rounded text-center"
                    min="0"
                    placeholder="0"
                  />
                </div>
              )}
            </div>
          );
        })}

        <div
          className={`mt-2 text-sm font-medium ${
            validation.isValid
              ? "text-green-600"
              : validation.isUnderMin
              ? "text-red-600"
              : validation.isOverMax
              ? "text-red-600"
              : "text-gray-600"
          }`}
        >
          Total Selected: {validation.total}/{min}-{max} servings
          {validation.isUnderMin && (
            <span className="ml-2 text-xs">
              (Need at least {min - validation.total} more)
            </span>
          )}
          {validation.isOverMax && (
            <span className="ml-2 text-xs">
              (Exceeded by {validation.total - max})
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title="Add Package"
        width={800}
        style={{ top: 20 }} // Keep this to control overall modal position
        styles={{
          body: { maxHeight: "calc(100vh - 180px)", overflowY: "auto" },
        }}
        // Add these two props for better modal behavior

        destroyOnClose={true}
        footer={
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit()}
              className={`px-6 py-2 rounded text-white ${
                canSubmit()
                  ? "bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:from-[#F9A022] hover:to-[#D06D01]"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              Add Package
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Choose Package <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedPackage}
              onChange={(e) => setSelectedPackage(e.target.value)}
              className="w-full p-3 border rounded"
              disabled={packagesLoading}
            >
              <option value="">-- Select package --</option>
              {packages.map((pkg) => (
                <option key={pkg.value} value={pkg.value}>
                  {pkg.label}
                </option>
              ))}
            </select>
          </div>

          {isLoadingDetails && selectedPackage && (
            <div className="text-center py-8 text-gray-500">
              Loading package details...
            </div>
          )}

          {packageDetsError && (
            <div className="text-center py-8 text-red-500">
              Error loading package details. Please try again.
            </div>
          )}

          {selectedPackage && !isLoadingDetails && categoryData.length > 0 && (
            <div className="border rounded p-4">
              <h3 className="font-medium mb-4">What's included:</h3>
              <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                <p className="text-blue-800">
                  <strong>Package Requirements:</strong> Each category must have
                  between <strong>{packageLimits.min}</strong> and{" "}
                  <strong>{packageLimits.max}</strong> servings. You can select
                  multiple items per category.
                </p>
              </div>

              {categoryData.map((category) => (
                // Important: Ensure category.id is stable and unique
                <CategorySection key={category.id} category={category} />
              ))}

              {Object.keys(selections).length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">
                      Estimated Total Amount:
                    </span>
                    <span className="text-lg font-bold text-orange-600">
                      ₦{calculateTotalAmount().toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default AddPackageModal;
